import { AlertV2, ButtonV2 } from '@cimplico/components';
import { ReactComponent as UploadIcon } from '../../assets/upload.svg';
import { downloadEmptyTrialBalanceTemplate, parseAsync, validateCsv } from './utils';
import { useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { PaperClipIcon, XMarkIcon } from '@heroicons/react/24/solid';
import Dropzone from '../../components/dropzone/Dropzone';
import FileInput from '../files/FileInput';
import { useExportTrialBalance } from '../../queries/use-trial-balance';
import PreviewChart from '../entity-overview/entity-content/chart-of-accounts/PreviewChartModal';
import { Entity } from '../../types/entity';
import TrialBalanceErrorAlert from './TrialBalanceErrorAlert';
export type TrialBalanceUploadErrors = {
    balance: { currentAmount: number; priorAmount: number; isBalanced: boolean };
    issues: { line: number; errors: { field: string; message: string }[] }[];
};

type TrialBalanceUploadProps = {
    workpaperId?: string;
    entity: Entity;
    onError: (err?: TrialBalanceUploadErrors) => void;
};

function TrialBalanceUpload({ workpaperId, entity, onError }: TrialBalanceUploadProps): JSX.Element {
    const downloadTBQuery = useExportTrialBalance();
    const [previewChart, setPreviewChart] = useState(false);
    const [error, _setError] = useState<TrialBalanceUploadErrors>();
    function download(): void {
        if (!workpaperId) {
            downloadEmptyTrialBalanceTemplate();
        } else {
            downloadTBQuery.mutate(workpaperId);
        }
    }

    const {
        formState: { errors },
        setValue,
        setError,
        clearErrors,
        watch,
    } = useFormContext();
    const uploadRef = useRef<HTMLInputElement | null>(null);

    const file = watch('file');

    async function handleFileSelect(files: File[]): Promise<void> {
        const file = files[0];

        clearErrors('trialBalance');
        setValue('file', undefined);
        setValue('file', file);

        if (!file) {
            setError('trialBalance', { message: 'A file must be uploaded.' });
            return;
        }

        if (file.type !== 'text/csv') {
            setError('trialBalance', { message: 'Invalid file type. File must be .csv extension.' });
            return;
        }

        const parsedFile = await parseAsync(file);

        // Check the file is an array of row objects
        if (!Array.isArray(parsedFile) || typeof parsedFile[0] !== 'object') {
            setError('trialBalance', {
                message: 'Invalid template - please ensure all template columns are present and there is at least 2 rows.',
            });
            return;
        }

        const columns = Object.keys(parsedFile[0]);
        // Ensure the upload csv has at least the required columns
        if (!columns || !['code', 'debitTY', 'creditTY', 'debitLY', 'creditLY'].every((header) => columns.includes(header))) {
            setError('trialBalance', {
                message: 'Invalid template - please ensure all template columns are present and there is at least 2 rows.',
            });
            return;
        }

        const res = validateCsv(parsedFile);

        if (!res.success) {
            _setError(res.error);
            setError('trialBalance', {
                message: 'The upload template contains invalid rows',
            });
            return;
        }
        setValue('trialBalance', res.data);
    }

    function removeSelectedFile(): void {
        setValue('file', undefined);
        setValue('trialBalance', undefined);
        setValue('chartOfAccounts', undefined);
        clearErrors('trialBalance');
        _setError(undefined);
    }

    function openFileInput(): void {
        if (uploadRef.current) {
            uploadRef.current.click();
        }
    }

    return (
        <div>
            {previewChart && <PreviewChart onClose={(): void => setPreviewChart(false)} entity={entity} />}

            <AlertV2
                type="info"
                title="We've changed how importing works."
                description="Ensure the entity has a valid chart of accounts before uploading the trial balance. If accounts in the trial balance are not found in the chart, the workpaper will fail to create."
            >
                <AlertV2.Action label="Verify chart" onClick={(): void => setPreviewChart(true)} />
                <AlertV2.ExternalLink label="Learn more" href="https://support.cimplico.com" />
            </AlertV2>
            {error && (
                <div className="mt-4">
                    <TrialBalanceErrorAlert errors={error} />
                </div>
            )}
            <FileInput innerRef={uploadRef} onSelect={handleFileSelect} />
            {file ? (
                <div className="mt-8">
                    <AlertV2
                        type="warn"
                        className="mb-4"
                        title="Important"
                        description="Any current accounts missing from this file will be permanently deleted along with any associated journals or files. If you wish to keep them, ensure the code is present."
                    />
                    <label className="text-sm font-medium text-gray-700 mb-1">Imported file</label>
                    <div className="flex items-center justify-between mt-2 text-sm">
                        <div className="flex">
                            <PaperClipIcon className="text-gray-700 h-5 w-5" />
                            <p className="pl-2">{file.name}</p>
                        </div>
                        <ButtonV2 leftIcon={<XMarkIcon />} onClick={removeSelectedFile} size="sm" type="hollow" />
                    </div>
                </div>
            ) : (
                <div className="mt-8">
                    <div className="flex justify-center">
                        <ButtonV2
                            type="primary"
                            label="Download Template"
                            onClick={download}
                            size="sm"
                            isDisabled={downloadTBQuery.isLoading}
                            isLoading={downloadTBQuery.isLoading}
                        />
                    </div>
                    <p className="flex justify-center text-gray-500 mt-2 text-sm">then</p>
                    <Dropzone onDrop={handleFileSelect}>
                        <div
                            className="flex flex-col items-center mt-2 border-dashed border border-gray-300 rounded-md p-4 cursor-pointer"
                            onClick={openFileInput}
                        >
                            <UploadIcon />
                            <p className="mt-2 text-sm">Upload filled-in template (.csv)</p>
                        </div>
                    </Dropzone>
                </div>
            )}
            <p className="text-sm mt-2 text-red-600">{errors.trialBalance?.message?.toString()}</p>
        </div>
    );
}
export default TrialBalanceUpload;
